import {
  graphNodeToModelBlock,
  isTemplateStepType,
  isToolStep,
  modelBlockToGraphNode,
  modelEdgeToGraphEdge,
} from 'utils/flow';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { removeDefaultPortId } from 'utils/defaultCanvasPortId';
import { mapAuthTypeForBackend } from 'utils/authMethodTypes';
import { getCreateAlertIntegrationUpdates } from 'utils/flow/createAlert';
import { integrationHasChanges } from 'utils/integrations';
import { getMergeTargetPort } from 'utils/flow/merge';
import {
  OUTPUT_MAPPINGS,
  builtInTypes,
  scriptStepTypes,
} from '@xmatters/common/constants/scriptSteps';
import {
  getPlanId,
  getApiIntegrationsById,
  getIntegrationsById,
} from 'selectors/flowDesigner';

import {
  isConditionStepEnabled,
  showTriggerFloodControl,
} from 'selectors/featureToggles';

import { v4 } from 'uuid';
import { IntegrationOperationsWithScript } from 'xm-constants/integration';
import { fetchScriptSteps, fetchScriptStep } from './scriptSteps';

export const LOAD_CANVAS_BY_PLAN = 'LOAD_CANVAS_BY_PLAN';
export const LOAD_CANVAS_BY_PLAN_START = `${LOAD_CANVAS_BY_PLAN}_START`;
export const LOAD_CANVAS_BY_PLAN_FINISHED = `${LOAD_CANVAS_BY_PLAN}_FINISHED`;
export const LOAD_CANVAS_BY_PLAN_SUCCESS = `${LOAD_CANVAS_BY_PLAN}_SUCCESS`;
export const LOAD_CANVAS_BY_PLAN_FAIL = `${LOAD_CANVAS_BY_PLAN}_FAIL`;

export const SAVE_CANVAS_BY_PLAN = 'SAVE_CANVAS_BY_PLAN';
export const SAVE_CANVAS_BY_PLAN_SUCCESS = `${SAVE_CANVAS_BY_PLAN}_SUCCESS`;
export const SAVE_CANVAS_BY_PLAN_FAIL = `${SAVE_CANVAS_BY_PLAN}_FAIL`;

export const FLOW_DESIGNER_SAVE = 'FLOW_DESIGNER_SAVE';
export const FLOW_DESIGNER_SAVE_START = `${FLOW_DESIGNER_SAVE}_START`;
export const FLOW_DESIGNER_SAVE_FINISH = `${FLOW_DESIGNER_SAVE}_FINISH`;

export const FETCH_PLAN_INTEGRATIONS = 'FETCH_ALL_INTEGRATIONS';
export const FETCH_PLAN_INTEGRATIONS_SUCCESS = `${FETCH_PLAN_INTEGRATIONS}_SUCCESS`;

export const FETCH_INITIAL_CANVAS_BY_PLAN = 'FETCH_INITIAL_CANVAS_BY_PLAN';
export const FETCH_INITIAL_CANVAS_BY_PLAN_START = `${FETCH_INITIAL_CANVAS_BY_PLAN}_START`;
export const FETCH_INITIAL_CANVAS_BY_PLAN_SUCCESS = `${FETCH_INITIAL_CANVAS_BY_PLAN}_SUCCESS`;
export const FETCH_INITIAL_CANVAS_BY_PLAN_FAIL = `${FETCH_INITIAL_CANVAS_BY_PLAN}_FAIL`;

export const FETCH_INITIAL_BLOCKS_BY_CANVAS = 'FETCH_INITIAL_BLOCKS_BY_CANVAS';
export const FETCH_INITIAL_BLOCKS_BY_CANVAS_START = `${FETCH_INITIAL_BLOCKS_BY_CANVAS}_START`;
export const FETCH_INITIAL_BLOCKS_BY_CANVAS_SUCCESS = `${FETCH_INITIAL_BLOCKS_BY_CANVAS}_SUCCESS`;
export const FETCH_INITIAL_BLOCKS_BY_CANVAS_FAIL = `${FETCH_INITIAL_BLOCKS_BY_CANVAS}_FAIL`;

export const FETCH_INITIAL_EDGES_BY_CANVAS = 'FETCH_INITIAL_EDGES_BY_CANVAS';
export const FETCH_INITIAL_EDGES_BY_CANVAS_START = `${FETCH_INITIAL_EDGES_BY_CANVAS}_START`;
export const FETCH_INITIAL_EDGES_BY_CANVAS_SUCCESS = `${FETCH_INITIAL_EDGES_BY_CANVAS}_SUCCESS`;
export const FETCH_INITIAL_EDGES_BY_CANVAS_FAIL = `${FETCH_INITIAL_EDGES_BY_CANVAS}_FAIL`;

export const FETCH_INITIAL_INPUTS_BY_CANVAS = 'FETCH_INITIAL_INPUTS_BY_CANVAS';
export const FETCH_INITIAL_INPUTS_BY_CANVAS_START = `${FETCH_INITIAL_INPUTS_BY_CANVAS}_START`;
export const FETCH_INITIAL_INPUTS_BY_CANVAS_SUCCESS = `${FETCH_INITIAL_INPUTS_BY_CANVAS}_SUCCESS`;
export const FETCH_INITIAL_INPUTS_BY_CANVAS_FAIL = `${FETCH_INITIAL_INPUTS_BY_CANVAS}_FAIL`;

export const FETCH_INITIAL_CANVAS_EMBEDS_DATA =
  'FETCH_INITIAL_CANVAS_EMBEDS_DATA';
export const FETCH_INITIAL_CANVAS_EMBEDS_DATA_START = `${FETCH_INITIAL_CANVAS_EMBEDS_DATA}_START`;
export const FETCH_INITIAL_CANVAS_EMBEDS_DATA_SUCCESS = `${FETCH_INITIAL_CANVAS_EMBEDS_DATA}_SUCCESS`;
export const FETCH_INITIAL_CANVAS_EMBEDS_DATA_FAIL = `${FETCH_INITIAL_CANVAS_EMBEDS_DATA}_FAIL`;

export const FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN =
  'FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN';
export const FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN_START = `${FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN}_START`;
export const FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN_SUCCESS = `${FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN}_SUCCESS`;
export const FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN_FAIL = `${FLOW_FETCH_PLAN_LAYOUT_SECTIONS_BY_PLAN}_FAIL`;

export const FLOW_FETCH_FORM_SECTIONS_ADD_AUTOMATION =
  'FLOW_FETCH_FORM_SECTIONS_ADD_AUTOMATION';
export const FLOW_FETCH_FORM_SECTIONS = 'FLOW_FETCH_FORM_SECTIONS';
export const FLOW_FETCH_FORM_SECTIONS_START = `${FLOW_FETCH_FORM_SECTIONS}_START`;
export const FLOW_FETCH_FORM_SECTIONS_SUCCESS = `${FLOW_FETCH_FORM_SECTIONS}_SUCCESS`;
export const FLOW_FETCH_FORM_SECTIONS_FAIL = `${FLOW_FETCH_FORM_SECTIONS}_FAIL`;

export const CREATE_FORM_SECTION = 'CREATE_FORM_SECTION';
export const CREATE_FORM_SECTION_START = 'CREATE_FORM_SECTION_START';
export const CREATE_FORM_SECTION_SUCCESS = 'CREATE_FORM_SECTION_SUCCESS';

export const FETCH_STEPS = 'FETCH_STEPS';
export const FETCH_STEPS_START = `${FETCH_STEPS}_START`;
export const FETCH_STEPS_SUCCESS = `${FETCH_STEPS}_SUCCESS`;
export const FETCH_STEPS_FAIL = `${FETCH_STEPS}_FAIL`;

export const FETCH_INTEGRATION_STEPS = 'FETCH_INTEGRATION_STEPS';
export const FETCH_INTEGRATION_STEPS_START = `${FETCH_INTEGRATION_STEPS}_START`;
export const FETCH_INTEGRATION_STEPS_SUCCESS = `${FETCH_INTEGRATION_STEPS}_SUCCESS`;
export const FETCH_INTEGRATION_STEPS_FAIL = `${FETCH_INTEGRATION_STEPS}_FAIL`;

export const FETCH_BUILT_IN_INPUTS = 'FETCH_BUILT_IN_INPUTS';
export const FETCH_BUILT_IN_INPUTS_START = `${FETCH_BUILT_IN_INPUTS}_START`;
export const FETCH_BUILT_IN_INPUTS_SUCCESS = `${FETCH_BUILT_IN_INPUTS}_SUCCESS`;
export const FETCH_BUILT_IN_INPUTS_FAIL = `${FETCH_BUILT_IN_INPUTS}_FAIL`;

export const FETCH_APPLICATION_FORMS = 'FETCH_APPLICATION_FORMS';
export const FETCH_APPLICATION_FORMS_START = `${FETCH_APPLICATION_FORMS}_START`;
export const FETCH_APPLICATION_FORMS_SUCCESS = `${FETCH_APPLICATION_FORMS}_SUCCESS`;
export const FETCH_APPLICATION_FORMS_FAIL = `${FETCH_APPLICATION_FORMS}_FAIL`;

export const FETCH_FORM_RESPONSES = 'FETCH_FORM_RESPONSES';
export const FETCH_FORM_RESPONSES_START = `${FETCH_FORM_RESPONSES}_START`;
export const FETCH_FORM_RESPONSES_SUCCESS = `${FETCH_FORM_RESPONSES}_SUCCESS`;
export const FETCH_FORM_RESPONSES_FAIL = `${FETCH_FORM_RESPONSES}_FAIL`;

export const FETCH_ALL_FORM_RESPONSES = 'FETCH_ALL_FORM_RESPONSES';
export const FETCH_ALL_FORM_RESPONSES_START = `${FETCH_ALL_FORM_RESPONSES}_START`;
export const FETCH_ALL_FORM_RESPONSES_SUCCESS = `${FETCH_ALL_FORM_RESPONSES}_SUCCESS`;
export const FETCH_ALL_FORM_RESPONSES_FAIL = `${FETCH_ALL_FORM_RESPONSES}_FAIL`;

export const ADD_BLOCK_TO_CANVAS = 'ADD_BLOCK_TO_CANVAS';
export const REMOVE_BLOCK_FROM_CANVAS = 'REMOVE_BLOCK_FROM_CANVAS';

export const ADD_NEW_INTEGRATION = 'ADD_NEW_INTEGRATION';
export const RESPONSE_TRIGGER_IN_CANVAS_CHANGED =
  'RESPONSE_TRIGGER_IN_CANVAS_CHANGED';

export const DELETE_PLAN_INTEGRATION = 'DELETE_PLAN_INTEGRATION';
export const DELETE_PLAN_INTEGRATION_START = `${DELETE_PLAN_INTEGRATION}_START`;
export const DELETE_PLAN_INTEGRATION_SUCCESS = `${DELETE_PLAN_INTEGRATION}_SUCCESS`;
export const DELETE_PLAN_INTEGRATION_FAIL = `${DELETE_PLAN_INTEGRATION}_FAIL`;

export const SAVE_PLAN_INTEGRATION = 'SAVE_PLAN_INTEGRATION';
export const SAVE_PLAN_INTEGRATION_START = `${SAVE_PLAN_INTEGRATION}_START`;
export const SAVE_PLAN_INTEGRATION_SUCCESS = `${SAVE_PLAN_INTEGRATION}_SUCCESS`;
export const SAVE_PLAN_INTEGRATION_FAIL = `${SAVE_PLAN_INTEGRATION}_FAIL`;

export const UPDATE_PLAN_INTEGRATION = 'UPDATE_PLAN_INTEGRATION';

export const FETCH_FLOWS = `FETCH_FLOWS`;
export const FETCH_FLOWS_START = `${FETCH_FLOWS}_START`;
export const FETCH_FLOWS_SUCCESS = `${FETCH_FLOWS}_SUCCESS`;
export const FETCH_FLOWS_FAIL = `${FETCH_FLOWS}_FAIL`;

export const FD_EDIT_INTEGRATION_START = 'FD_EDIT_INTEGRATION_START';
export const FD_EDIT_INTEGRATION_COMMIT = 'FD_EDIT_INTEGRATION_COMMIT';
export const FD_EDIT_INTEGRATION_END = 'FD_EDIT_INTEGRATION_END';

export const UPDATE_FORMS = `UPDATE_FORMS`;
export const UPDATE_FORMS_START = `${UPDATE_FORMS}_START`;
export const UPDATE_FORMS_SUCCESS = `${UPDATE_FORMS}_SUCCESS`;
export const UPDATE_FORMS_FAIL = `${UPDATE_FORMS}_FAIL`;

export const FLOW_DESIGNER_REBUILD_PLAN_CANVAS =
  'FLOW_DESIGNER_REBUILD_PLAN_CANVAS';

export const FD_SET_FORM_TRIGGER_INTEGRATION =
  'FD_SET_FORM_TRIGGER_INTEGRATION';

export const FD_EDIT_FORM = 'FD_EDIT_FORM';
export const FD_UPDATE_EDIT_FORM = 'FD_UPDATE_EDIT_FORM';
export const INCIDENT_TRIGGERS_CHANGED = 'INCIDENT_TRIGGERS_CHANGED';
export const FLOW_DESIGNER_DUPLICATE_BLOCK = 'FLOW_DESIGNER_DUPLICATE_BLOCK';
export const FLOW_DESIGNER_UPDATE_BLOCK_PRESENTATION =
  'FLOW_DESIGNER_UPDATE_BLOCK_PRESENTATION';
export const FLOW_DESIGNER_UPDATE_BLOCK_STEP =
  'FLOW_DESIGNER_UPDATE_BLOCK_STEP';
export const FLOW_DESIGNER_UPDATE_BLOCK_LABEL =
  'FLOW_DESIGNER_UPDATE_BLOCK_LABEL';
export const FLOW_DESIGNER_UPDATE_BLOCK_PORTS =
  'FLOW_DESIGNER_UPDATE_BLOCK_PORTS';
export const FLOW_DESIGNER_REMOVE_BLOCK_HIGHLIGHT =
  'FLOW_DESIGNER_REMOVE_BLOCK_HIGHLIGHT';
export const FLOW_DESIGNER_ADD_BLOCK_HIGHLIGHT =
  'FLOW_DESIGNER_ADD_BLOCK_HIGHLIGHT';
export const FLOW_DESIGNER_UPDATE_EDGE_CONNECT_HIGHLIGHT =
  'FLOW_DESIGNER_UPDATE_EDGE_CONNECT_HIGHLIGHT';

const CREATE_FORM = 'CREATE_FORM';
export const CREATE_FORM_SUCCESS = `${CREATE_FORM}_SUCCESS`;

const REFETCH_BLOCK_INPUTS = 'REFETCH_BLOCK_INPUTS';
export const REFETCH_BLOCK_INPUTS_SUCCESS = `${REFETCH_BLOCK_INPUTS}_SUCCESS`;

const canvasURI = '/webapi/xm/1/flow-canvases';

export const updateBlockPresentation = payload => ({
  payload,
  type: FLOW_DESIGNER_UPDATE_BLOCK_PRESENTATION,
});

export const updateEdgeConnectHightlight = payload => ({
  payload,
  type: FLOW_DESIGNER_UPDATE_EDGE_CONNECT_HIGHLIGHT,
});

export const updateBlockStep = ({ id, step }) => ({
  type: FLOW_DESIGNER_UPDATE_BLOCK_STEP,
  payload: { id, step },
});

export const updateBlockLabel = ({ id, label }) => ({
  type: FLOW_DESIGNER_UPDATE_BLOCK_LABEL,
  payload: { id, label },
});

export const updateBlockPorts = ({ id, ports, markCanvasDirty = true }) => ({
  payload: { id, ports, markCanvasDirty },
  type: FLOW_DESIGNER_UPDATE_BLOCK_PORTS,
});

export const removeDuplicateBlockHighlight = payload => ({
  payload,
  type: FLOW_DESIGNER_REMOVE_BLOCK_HIGHLIGHT,
});

export const addDuplicateBlockHighlight = payload => ({
  payload,
  type: FLOW_DESIGNER_ADD_BLOCK_HIGHLIGHT,
});

export const rebuildPlanCanvas = () => ({
  type: FLOW_DESIGNER_REBUILD_PLAN_CANVAS,
});

export const fetchResponsesForForm = (
  planId,
  formId,
  type = 'FETCH_FORM_RESPONSES',
) => ({
  type,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/plans/${planId}/forms/${formId}/response-options`,
    method: 'get',
  },
});

const fetchBlocksByCanvasId = ({ canvasId }) => ({
  type: FETCH_INITIAL_BLOCKS_BY_CANVAS,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/flow-blocks`,
    method: 'getall',
    params: {
      flowCanvas: canvasId,
      limit: 1000,
    },
  },
});

const fetchEdgesByCanvasId = ({ canvasId }) => ({
  type: FETCH_INITIAL_EDGES_BY_CANVAS,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/flow-edges`,
    method: 'getall',
    params: {
      flowCanvas: canvasId,
    },
  },
});

const fetchInputsByCanvasId = ({ canvasId }) => ({
  type: FETCH_INITIAL_INPUTS_BY_CANVAS,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/flow-block-inputs`,
    method: 'getall',
    params: {
      flowCanvasId: canvasId,
    },
  },
});

export const refetchBlockInputs = ({ blockId }) => ({
  type: REFETCH_BLOCK_INPUTS,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/flow-block-inputs`,
    method: 'getall',
    params: {
      flowBlockId: blockId,
    },
  },
  payload: { blockId },
});

const fetchCanvasEmbedsDataById = ({ canvasId }) => {
  return (dispatch, getState) => {
    let embed = 'switchCases,mergeOutputs,dynamicOutputs';
    if (isConditionStepEnabled(getState())) {
      embed += ',conditionPaths';
    }

    return dispatch({
      type: FETCH_INITIAL_CANVAS_EMBEDS_DATA,
      meta: {
        xmApi: true,
        uri: `/webapi/xm/1/flow-canvases/${canvasId}`,
        method: 'getAllWithEmbeds',
        params: {
          embed,
        },
      },
    });
  };
};

export function fetchPlanIntegrations(planId) {
  return {
    type: FETCH_PLAN_INTEGRATIONS,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/plans/${planId}/integrations`,
      method: 'getall',
      params: {
        limit: 1000,
      },
    },
  };
}

const fetchIntegrationSteps = () => ({
  type: FETCH_INTEGRATION_STEPS,
  meta: {
    xmApi: true,
    uri: `/webapi/xm/1/script-steps`,
    method: 'getall',
    params: {
      scriptStepType: 'BUILT_IN',
    },
  },
});

export const fetchBuiltInInputs = () => ({
  type: FETCH_BUILT_IN_INPUTS,
  meta: {
    // TODO: uncomment once the endpoint is implemented
    // xmApi: true,
    uri: `/webapi/xm/1/built-in-inputs`,
    method: 'getall',
  },
});

export function updateForms({ planId, form }) {
  return {
    type: UPDATE_FORMS,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/plans/${planId}/forms`,
      method: 'post',
      params: form,
    },
  };
}

// Identical as updateForms, but we need a different action type for reducers.
export function createForm({ planId, form }) {
  return {
    type: CREATE_FORM,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/plans/${planId}/forms`,
      method: 'post',
      params: form,
    },
  };
}

// Forms created using xm-api (createForm) does not contain sections (outputs) by default,
// unlike GWT. Need to use this action to manually create sections.
export function createFormSection(formId, { isAutomation, ...section }) {
  return {
    type: CREATE_FORM_SECTION,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/forms/${formId}/sections`,
      method: 'post',
      params: section,
    },
    payload: { isAutomation },
  };
}

export function fetchFormSections(formId, isAutomationTrigger) {
  return dispatch => {
    dispatch({
      type: FLOW_FETCH_FORM_SECTIONS_ADD_AUTOMATION,
      payload: isAutomationTrigger,
    });
    return dispatch({
      type: FLOW_FETCH_FORM_SECTIONS,
      meta: {
        xmApi: true,
        uri: `/webapi/xm/1/forms/${formId}/sections`,
        method: 'getall',
      },
    });
  };
}

export function loadStepsByPlanId({ planId }) {
  return dispatch => {
    dispatch({ type: FETCH_STEPS_START });

    return Promise.all([
      dispatch(fetchIntegrationSteps()),
      dispatch(fetchScriptSteps(planId)),
    ])
      .then(() => dispatch({ type: FETCH_STEPS_SUCCESS }))
      .catch(() => dispatch({ type: FETCH_STEPS_FAIL }));
  };
}

function getCanvasObjects({ planId, canvasId }) {
  return (dispatch, getState) => {
    const {
      flowDesigner: { allCanvases },
    } = getState();

    let canvas;
    if (allCanvases && allCanvases.length > 0) {
      canvas = allCanvases.find(c => c.id === canvasId);
    }

    if (typeof canvas === 'undefined') return Promise.reject();

    const fetchBlocksEdgesResponses = canvas.initial
      ? Promise.resolve()
      : Promise.all([
          dispatch(fetchBlocksByCanvasId({ canvasId: canvas.id })),
          dispatch(fetchEdgesByCanvasId({ canvasId: canvas.id })),
          dispatch(fetchInputsByCanvasId({ canvasId: canvas.id })),
          dispatch(fetchCanvasEmbedsDataById({ canvasId: canvas.id })),
        ]);

    const fetchResponseAndIntegrationActions = [];
    if (canvas && canvas.form) {
      // if canvas has form, we should load responses
      fetchResponseAndIntegrationActions.push(
        dispatch(fetchResponsesForForm(planId, canvas.form.id)),
      );
    }
    const fetchResponsesAndIntegrations = Promise.all(
      fetchResponseAndIntegrationActions,
    );

    return Promise.all([
      fetchBlocksEdgesResponses,
      fetchResponsesAndIntegrations,
    ]).then(() =>
      dispatch({
        type: LOAD_CANVAS_BY_PLAN_SUCCESS,
        payload: {
          planId,
          canvas,
        },
      }),
    );
  };
}

export function loadCanvasByCanvasId({ planId, canvasId }) {
  return dispatch => {
    dispatch({ type: LOAD_CANVAS_BY_PLAN_START });
    const canvasPromise = dispatch(getCanvasObjects({ planId, canvasId }));
    return canvasPromise
      .catch(() =>
        dispatch({
          type: LOAD_CANVAS_BY_PLAN_FAIL,
          payload: {
            error: true,
          },
        }),
      )
      .finally(() => dispatch({ type: LOAD_CANVAS_BY_PLAN_FINISHED }));
  };
}

export function saveCanvas({ canvas }) {
  return (dispatch, getState) => {
    let embed = 'switchCases';
    if (isConditionStepEnabled(getState())) {
      embed += ',conditionPaths';
    }

    return dispatch({
      type: SAVE_CANVAS_BY_PLAN,
      meta: {
        xmApi: true,
        method: 'put',
        uri: `${canvasURI}?embed=${embed}`,
        params: canvas,
      },
    });
  };
}

export const addBlockToCanvas = ({ block, inputNameValueMap }) => ({
  type: ADD_BLOCK_TO_CANVAS,
  payload: { block, inputNameValueMap },
});

export const removeBlockFromCanvas = block => ({
  type: REMOVE_BLOCK_FROM_CANVAS,
  payload: { block },
});

export const ADD_EDGE_TO_CANVAS = 'ADD_EDGE_TO_CANVAS';
export const addEdgeToCanvas = edge => ({
  type: ADD_EDGE_TO_CANVAS,
  payload: { edge },
});

export const REMOVE_EDGE_FROM_CANVAS = 'REMOVE_EDGE_FROM_CANVAS';
export const removeEdgeFromCanvas = edgeId => ({
  type: REMOVE_EDGE_FROM_CANVAS,
  payload: { edgeId },
});

export const BULK_GRAPH_MANIPULATION = 'BULK_GRAPH_MANIPULATION';
export const bulkGraphManipulation = ({
  edgesToAdd,
  edgeIdsToRemove,
  blockPositions,
}) => ({
  type: BULK_GRAPH_MANIPULATION,
  payload: {
    edgesToAdd,
    edgeIdsToRemove,
    blockPositions,
  },
});

export const duplicateBlock = ({
  blockId,
  blockLabelBuilder,
  stepType,
  xOffset,
  yOffset,
  intl,
}) => ({
  type: FLOW_DESIGNER_DUPLICATE_BLOCK,
  blockLabelBuilder,
  payload: {
    blockId,
    blockLabelBuilder,
    stepType,
    xOffset,
    yOffset,
    intl,
  },
});

export function addIntegration({ integration }) {
  return {
    type: ADD_NEW_INTEGRATION,
    payload: { integration },
  };
}

export const MARK_INTEGRATION_DELETED = 'MARK_INTEGRATION_DELETED';
export const markIntegrationDeleted = ({ blockIds, blocks }) => ({
  type: MARK_INTEGRATION_DELETED,
  payload: { blockIds, blocks },
});

export function deleteIntegration({ planId, id }) {
  return {
    type: DELETE_PLAN_INTEGRATION,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/plans/${planId}/integrations/${id}`,
      method: 'del',
    },
  };
}

function sanitizeTriggerFilter(triggerFilter) {
  if (!triggerFilter) {
    return undefined;
  }

  const result = { ...triggerFilter };
  const config = result?.config;
  if (config) {
    // empty or 0 as floodCallbackIntervalMinutes imply no callback, same for floodCallbackUrl.
    // In these cases, remove both fields (xm-api doesn't like `null` and similar):
    if (!config.floodCallbackIntervalMinutes) {
      delete config.floodCallbackUrl;
    }
    if (!config.floodCallbackUrl) {
      delete config.floodCallbackIntervalMinutes;
    }
  }
  return result;
}

export const updateIntegration = ({ id, props }) => ({
  type: UPDATE_PLAN_INTEGRATION,
  payload: { id, props },
});

export function saveIntegration({
  planId,
  integration,
  forceRefetchScriptStep,
}) {
  return (dispatch, getState) => {
    const hasTriggerFilter = showTriggerFloodControl(getState());
    const { authenticationType, environment, triggerFilter, ...payload } =
      integration;

    // `...payload` already creates a fresh Object, can reassign `.triggerFilter` safely:
    if (hasTriggerFilter) {
      const saneTriggerFilter = sanitizeTriggerFilter(triggerFilter);
      if (saneTriggerFilter && !isEmpty(saneTriggerFilter)) {
        payload.triggerFilter = saneTriggerFilter;
      }
    }

    // avoid validation.communication_plan.integrations.script_not_required error in some situations
    if (!IntegrationOperationsWithScript.has(integration.operation)) {
      delete payload.script;
    }

    return dispatch({
      type: SAVE_PLAN_INTEGRATION,
      meta: {
        xmApi: true,
        uri: `/webapi/xm/1/plans/${planId}/integrations`,
        method: 'post',
        params: {
          authenticationType: mapAuthTypeForBackend(authenticationType),
          environment: environment === 'ANY' ? 'HOSTED' : environment,
          ...payload,
        },
      },
    }).then(() => {
      const apiIntegrationsById = getApiIntegrationsById(getState());
      const apiIntegration = apiIntegrationsById[integration.id];
      const oldScriptStepId = integration?.scriptStep?.id;
      const newScriptStepId = apiIntegration?.scriptStep?.id;
      const scriptStepIdChanged =
        newScriptStepId && newScriptStepId !== oldScriptStepId;

      return forceRefetchScriptStep || scriptStepIdChanged
        ? dispatch(fetchScriptStep(newScriptStepId))
        : undefined;
    });
  };
}

export function updateAndSaveIntegration({
  id,
  props,
  forceRefetchScriptStep,
}) {
  return (dispatch, getState) => {
    dispatch(updateIntegration({ id, props }));
    const planId = getPlanId(getState());
    const integrationsById = getIntegrationsById(getState());
    const integration = integrationsById[id];
    return dispatch(
      saveIntegration({ planId, integration, forceRefetchScriptStep }),
    );
  };
}

export function loadFlowsByPlanId({ planId }) {
  return {
    type: FETCH_FLOWS,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/plans/${planId}`,
      method: 'getAllWithEmbeds',
      params: {
        embed: 'forms,integrations,flow-canvases',
      },
    },
  };
}

export function startEditIntegration(integrationId, flowBlockId) {
  return {
    type: FD_EDIT_INTEGRATION_START,
    payload: { integrationId, flowBlockId },
  };
}

export function commitIntegration(id, props) {
  return {
    type: FD_EDIT_INTEGRATION_COMMIT,
    payload: { ...props, id },
  };
}

export function endEditIntegration() {
  return {
    type: FD_EDIT_INTEGRATION_END,
  };
}

const EXPECTED_STEP_TYPES = new Set([
  scriptStepTypes.SCRIPT_STEP,
  scriptStepTypes.SWITCH,
  scriptStepTypes.CREATE_EVENT,
  scriptStepTypes.CREATE_ALERT,
  scriptStepTypes.RESPONSES,
  scriptStepTypes.MERGE,
  scriptStepTypes.TEMPLATE_EBFD_LAUNCH_INCIDENT_STEP,
  scriptStepTypes.EBFD_LAUNCH_INCIDENT_STEP,
  scriptStepTypes.TEMPLATE_EBFD_UPDATE_INCIDENT_STEP,
  scriptStepTypes.EBFD_UPDATE_INCIDENT_STEP,
  scriptStepTypes.TEMPLATE_EBFD_CLOSE_INCIDENT_STEP_V2,
  scriptStepTypes.EBFD_CLOSE_INCIDENT_STEP_V2,
]);

/*
 * Used because across our validations and similar we rely on `scriptStepInput` value, because it holds both
 * A) The identifier (scriptStepInput.id)
 * B) Data our forms will use to render the inputs
 * However API is more correct: for formPropertyInputs it only has `formSectionItem` as its reference.
 *
 * Easiest place to achieve consistency for now is at the ends (on fetch, and before save)
 * Not super nice modelling, but it's what we have. TODO: try to iron out UI side to be equally correct.
 */
const prepareInputs = canvas => {
  const { inputs = [], blocks } = canvas;
  const preparedInputs = inputs.map(input => {
    if (
      input.scriptStepInput &&
      input.value &&
      typeof input.value === 'object'
    ) {
      return {
        ...input,
        value: JSON.stringify(input.value),
      };
    }
    return input;
  });
  return preparedInputs
    .filter(input => blocks.find(b => b.id === input.flowBlock.id))
    .map(input => {
      // if this isn't catching everything use `|| get(input, 'formSectionItem.id')`.
      // DO NOT just check input.formSectionItem since xm-api is sending it back as an
      // empty Object (truthy) for non-form-section inputs.
      if (input.scriptStepInput && input.scriptStepInput.isFormSectionItem) {
        return {
          ...input,
          formSectionItem: {
            id: input.scriptStepInput.id,
          },
          scriptStepInput: undefined,
        };
      }
      if (input.scriptStepInput && input.scriptStepInput.isBuiltInInput) {
        return {
          ...input,
          builtInInput: {
            id: input.scriptStepInput.id,
          },
          scriptStepInput: undefined,
        };
      }
      if (input.mergeOutput) {
        return {
          ...input,
          mergeOutput: input.mergeOutput,
          scriptStepInput: undefined,
        };
      }
      // "dynamic outputs" is an input
      // We let user create all the empty dynamic outputs their heart desire
      // but we nuke 'em upon saving.
      // This happens both here in canvas.inputs
      // and also in canvas.dynamicOutputs
      if (input.value && input.scriptStepInput?.name === OUTPUT_MAPPINGS) {
        const dynOs = JSON.parse(input.value);
        return {
          ...input,
          value: JSON.stringify(
            dynOs
              .filter(dynO => dynO.name && dynO.dataOriginPointer)
              .map((dynO, i) => ({ ...dynO, position: i })),
          ),
        };
      }
      return input;
    });
};

// Even though the payload contains everything xmApi could need to derive the dynamicOutputs
// we're duplicating the data in a top-level property, solely in order to keep xmApi simpler.
// So this bit adds dynamic outputs so that xmApi adds them to the integration_script_step_outputs
// table in the DB.
const prepareDynamicOutputs = canvas =>
  canvas.inputs.reduce((dynamicOutputMappings, input) => {
    if (input.value && input.scriptStepInput?.name === OUTPUT_MAPPINGS) {
      const dynOs = JSON.parse(input.value);
      dynOs.forEach(dynO => {
        if (dynO.name && dynO.dataOriginPointer) {
          dynamicOutputMappings.push({
            id: dynO.id,
            name: dynO.name,
            description: dynO.description,
            position: dynO.position,
            scriptStep: {
              id: input.scriptStepInput.scriptStep.id,
            },
            flowBlock: {
              id: input.flowBlock.id,
            },
          });
        }
      });
    }
    return dynamicOutputMappings;
  }, []);

const getToolStepSaveData = step => {
  const { id, type } = step;
  if (step.type === scriptStepTypes.CREATE_EVENT && id) {
    // Create event must use a formId (set in VisualizationCanvas.jsx already).
    // Form ID may be different from the current canvases one (may target other forms):
    return { id, type };
  }
  // Most tools don't allow ID:
  return { type };
};

function getBlocksForAPI({ endpoints, graphData, planCanvas }) {
  const nodeBlocks = graphData.nodes.map(node => {
    const endpoint = endpoints.find(e => e.flowBlockId === node.id);
    const block = graphNodeToModelBlock(node);

    if (endpoint) {
      block.endpoint = endpoint.endpoint;
    }

    block.presentation.x = node.left;
    block.presentation.y = node.top;

    return block;
  });

  // Do not include duplicates:
  const seenBlockIds = new Set();
  const allBlocks = [
    ...nodeBlocks,
    // Add in hidden blocks (Create Alert triggers, responses, Initiate Incident sub-blocks, ...)
    // These are not sent to JSPlumb so will not exist in the toolkit's graphData.nodes:
    ...filter(planCanvas.blocks, 'groupingBlockId'),
  ].filter(block => {
    if (seenBlockIds.has(block.id)) {
      return false;
    }
    seenBlockIds.add(block.id);
    return true;
  });

  // TODO: try to get IB (xm-script-executor) to support these... Essentially the presence of a
  // groupingBlockId prevents any access to form-id outputs. Ideally both should be populated.
  const blocksById = keyBy(allBlocks, 'id');
  return allBlocks.map(block => {
    const groupingBlock =
      block.groupingBlockId && blocksById[block.groupingBlockId];
    if (groupingBlock?.step?.type === scriptStepTypes.RESPONSES) {
      return {
        ...block,
        groupingBlockId: null,
      };
    }
    return block;
  });
}

function diffIntegrations(apiIntegrations, uiIntegrations, canvas) {
  const currentIntegsById = keyBy(uiIntegrations, 'id');
  const apiIntegsById = keyBy(apiIntegrations, 'id');

  return {
    integrationsToDelete: apiIntegrations.filter(
      integration => !currentIntegsById[integration.id],
    ),
    integrationsToSave: [
      ...uiIntegrations.filter(integration =>
        integrationHasChanges(integration, apiIntegsById[integration.id]),
      ),
      ...getCreateAlertIntegrationUpdates(canvas.blocks, uiIntegrations),
    ],
  };
}

export const exportToolkitCanvas = graphData => (dispatch, getState) => {
  const state = getState();
  const {
    planCanvas,
    planId,
    endpoints,
    apiIntegrations,
    uiIntegrations,
    modifiedForms,
    mergeOutputs,
    switchCases,
  } = state.flowDesigner;

  dispatch({ type: FLOW_DESIGNER_SAVE_START });

  const canvas = cloneDeep(planCanvas);
  canvas.blocks = getBlocksForAPI({
    endpoints,
    graphData,
    planCanvas,
  });

  const switchBlockIds = new Set(
    canvas.blocks
      .filter(
        ({ step }) =>
          step?.type === scriptStepTypes.SWITCH ||
          step?.type === scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
      )
      .map(block => block.id),
  );
  canvas.switchCases = switchCases.filter(switchCase =>
    switchBlockIds.has(switchCase?.block.id),
  );
  delete canvas.conditionPathsByBlockId;
  delete canvas.incidentProperties;
  canvas.conditionPaths = Object.values(
    planCanvas.conditionPathsByBlockId ?? {},
  ).flat();

  const mergeBlocks = canvas.blocks.filter(({ step }) => step.type === 'MERGE');

  const allPorts = (graphData.ports || []).concat(
    flatMap(canvas.blocks, 'ports').filter(port => !!port),
  );
  const pseudoBlockOutputPorts = allPorts
    .filter(p => {
      // Generally our models use "portType", `p.type` may not be necessary:
      const type = p.type || p.portType;
      return type === 'responseStart' || type === 'pseudoBlockOutput';
    })
    // `p.id` is the legacy jsplumb way, we generally model it with p.jsPlumbId in the frontend.
    // Note that `p.jsPlumbId` is not exactly equal, it is just `$PORTID` where old jsplumb p.id were `$BLOCKID.$PORTID`
    .map(p => p.jsPlumbId ?? p.id);

  canvas.edges = graphData.edges.map(edge => {
    const source = removeDefaultPortId('source', edge.source);
    const target = removeDefaultPortId('target', edge.target);
    const [sourceBlock, sourcePort] = source.split('.');
    const targetSplit = target.split('.');
    const targetBlock = targetSplit[0];
    let targetPort = targetSplit[1];
    const isPseudoBlockEdge =
      pseudoBlockOutputPorts.includes(source) ||
      // Since jsPlumbId was not the whole `$BLOCKID.$PORTID` it won't have matched the whole source...
      pseudoBlockOutputPorts.includes(sourcePort);

    const isBlockSource =
      !sourcePort || isPseudoBlockEdge || sourceBlock === sourcePort;
    const isBlockTargetMergeBlock =
      mergeBlocks.findIndex(block => block.id === targetBlock) !== -1;
    targetPort = isBlockTargetMergeBlock
      ? getMergeTargetPort(source, canvas.blocks)
      : targetPort;
    const isBlockTarget = !targetPort || targetBlock === targetPort;

    const from = {
      block: {
        id: isPseudoBlockEdge && sourcePort ? sourcePort : sourceBlock,
      },
      port: isBlockSource ? 'output' : sourcePort,
    };
    const to = {
      block: {
        id: targetBlock,
      },
      port: isBlockTarget ? 'input' : targetPort,
    };

    if (edge.data.origin) {
      return {
        ...edge.data.origin,
        from,
        to,
      };
    }
    return {
      ...edge.data,
      from,
      to,
    };
  });

  const { integrationsToSave, integrationsToDelete } = diffIntegrations(
    apiIntegrations,
    uiIntegrations,
    canvas,
  );

  const deletedIntegrationIds = uniq(map(integrationsToDelete, 'id'));
  const deleteIntegrations = () =>
    deletedIntegrationIds.map(id =>
      dispatch(deleteIntegration({ planId, id })),
    );

  canvas.blocks = canvas.blocks
    .filter(
      b =>
        isToolStep(b.step) ||
        (!b.step.id && b.step.type === 'RESPONSES') ||
        (b.step.id && !deletedIntegrationIds.includes(b.step.id)),
    )
    .map(b => {
      let type;
      // We altered Initiate Incident block's step.type to XMATTERS_CREATE_INCIDENT in replaceInitiateIncidentBlock.js from SCRIPT_STEP,
      // so we can track it easily. However, still have
      // to tell server it's actually still SCRIPT_STEP so server won't complain.
      if (b.step.type === builtInTypes.XMATTERS_CREATE_INCIDENT) {
        type = scriptStepTypes.SCRIPT_STEP;
      } else if (EXPECTED_STEP_TYPES.has(b.step.type)) {
        type = b.step.type;
      } else {
        type = 'INTEGRATION';
      }

      const step = isToolStep(b.step)
        ? getToolStepSaveData(b.step)
        : { ...b.step, type };

      const environment = b.environment === 'ANY' ? 'HOSTED' : b.environment;

      return {
        ...b,
        step,
        environment,
      };
    });
  canvas.inputs = prepareInputs(canvas);
  canvas.mergeOutputs = mergeOutputs;
  canvas.dynamicOutputs = [
    ...prepareDynamicOutputs(canvas),
    ...flatMap(planCanvas.incidentProperties),
  ];

  const uniqToSave = uniqBy(integrationsToSave, 'id');
  const saveIntegrations = () =>
    uniqToSave.map(integration =>
      dispatch(saveIntegration({ planId, integration })),
    );

  // Will return empty Array if there are no modifiedForms, which resolves for Promise.all:
  const saveForms = () =>
    map(modifiedForms, form => dispatch(updateForms({ planId, form })));

  const fetchNewTemplateInstanceSteps = ({ oldBlocks, newBlocks }) => {
    const oldBlocksById = keyBy(oldBlocks, 'id');
    const newStepIds = newBlocks
      .filter(block => {
        const oldBlock = oldBlocksById[block.id];
        return (
          isTemplateStepType(oldBlock.step.type) &&
          block.step.type !== oldBlock.step.type &&
          block.step?.id
        );
      })
      .map(block => block.step.id);
    const uniqueNewStepIds = uniq(newStepIds);

    return uniqueNewStepIds.map(stepId => dispatch(fetchScriptStep(stepId)));
  };

  return Promise.all(deleteIntegrations())
    .then(() => Promise.all(saveIntegrations()))
    .then(() => Promise.all(saveForms()))
    .then(() => dispatch(saveCanvas({ canvas })))
    .then(({ payload }) =>
      // saving template steps can create new instance steps that need to be fetched
      Promise.all(
        fetchNewTemplateInstanceSteps({
          oldBlocks: canvas.blocks,
          newBlocks: payload.blocks.data,
        }),
      ),
    )
    .then(() => dispatch({ type: FLOW_DESIGNER_SAVE_FINISH }));
};

export const exportPlanCanvas = () => (dispatch, getState) => {
  const { planCanvas, responses } = getState().flowDesigner;

  const parentResponseTriggerBlock = (planCanvas.blocks || []).find(
    block => get(block, 'step.type') === 'RESPONSES',
  );
  const graphPorts = parentResponseTriggerBlock
    ? (responses || []).map(r => ({
        id: `${parentResponseTriggerBlock.id}.${r.id}`,
        portType: 'responseStart',
      }))
    : [];

  // TODO: saving should ideally use entirely API-like redux data, not depend on mimicking JsPlumb data formats...
  return exportToolkitCanvas({
    nodes: (planCanvas.blocks || []).map(modelBlockToGraphNode),
    edges: (planCanvas.edges || []).map(modelEdgeToGraphEdge),
    groups: [],
    ports: graphPorts,
  })(dispatch, getState);
};

export const setFormFlowTrigger = ({ formId, flowBlockId, label }) => ({
  type: FD_SET_FORM_TRIGGER_INTEGRATION,
  formId,
  flowBlockId,
  label,
});

export function editFormEnables(form) {
  return {
    type: FD_EDIT_FORM,
    payload: form,
  };
}

export function updateFormEnables(id, uiEnabled, mobileEnabled) {
  return {
    type: FD_UPDATE_EDIT_FORM,
    payload: { id, uiEnabled, mobileEnabled },
  };
}

export function onIncidentTriggersChanged(flowBlockId, value) {
  return {
    type: INCIDENT_TRIGGERS_CHANGED,
    payload: { flowBlockId, value },
  };
}

export function recreateTriggerBlockIntegration(block, integration) {
  return dispatch => {
    dispatch(
      deleteIntegration({
        planId: integration.plan.id,
        id: integration.id,
      }),
    );

    const newIntegrationId = v4();
    dispatch(
      addIntegration({
        integration: {
          ...integration,
          id: newIntegrationId,
          name: integration.name.replace(integration.id, newIntegrationId),
        },
      }),
    );

    dispatch(
      updateBlockStep({
        id: block.id,
        step: { ...block.step, id: newIntegrationId },
      }),
    );
  };
}
